import { supabase } from "@/integrations/supabase/client";
import { compressImageFile } from "@/lib/image-compress";

/** Storage uploads that stall longer than this fail loudly instead of hanging. */
const UPLOAD_TIMEOUT_MS = 60_000;

export async function withUploadTimeout<T>(p: PromiseLike<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out — check your connection and try again`)),
      UPLOAD_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([Promise.resolve(p), timeout]);
  } finally {
    clearTimeout(timer!);
  }
}

export type TrailerPhotoType = "trailer" | "seal" | "license_plate";

/**
 * Upload a typed trailer photo to the `trailer-images` bucket, insert a
 * `trailer_event_photos` row, and — for the legacy `trailer` type — mirror
 * the path back onto `trailers.image_path` so pre-gallery readers (Yard
 * Audit thumbnail, Trailers page, etc.) keep rendering the newest capture.
 */
export async function uploadTrailerEventPhoto(params: {
  eventId: string;
  trailerDbId: string;
  file: File;
  photoType: TrailerPhotoType;
  tenantId?: string | null;
  userId?: string | null;
  /**
   * Optional storage-path prefix used in place of `photoType` when building
   * the filename. Lets callers distinguish variants of the same photo_type
   * (e.g. `trailer-left` vs `trailer-right`) without expanding the DB enum.
   */
  pathVariant?: string;
}): Promise<void> {
  const { eventId, trailerDbId, file: rawFile, photoType, tenantId, userId, pathVariant } = params;
  // Downscale the camera capture before it goes over the wire; falls back to
  // the original file when the browser can't decode/encode it.
  const file = await compressImageFile(rawFile);
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const rand = Math.random().toString(36).substring(7);
  const prefix = pathVariant || photoType;
  const path = `event/${eventId}/${prefix}-${Date.now()}-${rand}.${ext}`;

  const { error: upErr } = await withUploadTimeout(
    supabase.storage
      .from("trailer-images")
      .upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type || undefined }),
    `${photoType} photo upload`,
  );
  if (upErr) {
    console.error(`Yard ${photoType} photo upload error:`, upErr);
    throw upErr;
  }

  const payload: Record<string, unknown> = {
    trailer_event_id: eventId,
    trailer_id: trailerDbId,
    photo_type: photoType,
    image_path: path,
    uploaded_by: userId ?? null,
  };
  if (tenantId) payload.tenant_id = tenantId;

  const { error: insErr } = await supabase
    .from("trailer_event_photos" as never)
    .insert(payload as never);
  if (insErr) {
    console.error(`trailer_event_photos insert error (${photoType}):`, insErr);
    // Best-effort cleanup so we don't leak orphaned storage objects.
    await supabase.storage.from("trailer-images").remove([path]).catch(() => undefined);
    throw insErr;
  }

  // Back-compat mirror: legacy readers still point at trailers.image_path.
  if (photoType === "trailer") {
    const { error: updErr } = await supabase
      .from("trailers")
      .update({ image_path: path })
      .eq("id", trailerDbId);
    if (updErr) {
      console.error("Trailer image_path mirror update error:", updErr);
    }
  }
}

/**
 * Upload a yard photo to the trailer-images bucket and
 * update the trailer's image_path.
 *
 * @deprecated Prefer `uploadTrailerEventPhoto` which links the file to a
 * specific check-in event and records a photo type. Kept for callers that
 * only need to overwrite the trailer's latest image pointer.
 */
export async function uploadYardPhoto(
  trailerDbId: string,
  photo: File
): Promise<void> {
  const ext = photo.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileName = `${trailerDbId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from("trailer-images")
    .upload(fileName, photo, { cacheControl: "3600", upsert: false });

  if (uploadError) {
    console.error("Photo upload error:", uploadError);
    // Surface the failure to the caller so the check-in flow can warn the
    // operator instead of silently leaving `trailers.image_path` null.
    throw uploadError;
  }

  const { error: updateError } = await supabase
    .from("trailers")
    .update({ image_path: fileName })
    .eq("id", trailerDbId);
  if (updateError) {
    console.error("Trailer image_path update error:", updateError);
    throw updateError;
  }
}

/**
 * Upload a BOL photo to idp-uploads bucket, link it to a trailer event
 * via the trailer_event_documents table, AND run the IDP pipeline so it
 * appears in Document History.
 *
 * NOTE: This function does NOT push the BoL to the NetSuite-facing S3
 * bucket. The S3 sync now fires only when a BoL is attached/uploaded to
 * a specific order. If this trailer is later assigned to an order, the
 * `sync-trailer-bol-to-order` edge function will pick up this document.
 */
export async function uploadBolPhoto(
  eventId: string,
  rawPhoto: File,
  userId?: string,
  userEmail?: string,
  tenantId?: string
): Promise<void> {
  if (!eventId) return;

  const photo = await compressImageFile(rawPhoto);
  const ext = photo.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileName = `bol/${eventId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;

  const { error: uploadError } = await withUploadTimeout(
    supabase.storage
      .from("idp-uploads")
      .upload(fileName, photo, { cacheControl: "3600", upsert: false, contentType: photo.type || undefined }),
    "BOL photo upload",
  );

  if (uploadError) {
    console.error("BOL photo upload error:", uploadError);
    return;
  }

  // Link to trailer event
  const docPayload: any = {
    trailer_event_id: eventId,
    file_path: fileName,
    file_name: photo.name || `bol-${Date.now()}.${ext}`,
    document_type: "bol",
  };
  if (tenantId) docPayload.tenant_id = tenantId;

  const { error: insertError } = await supabase
    .from("trailer_event_documents")
    .insert(docPayload);

  if (insertError) {
    console.error("BOL document link error:", insertError);
  }

  // Run IDP pipeline so the document appears in Document History
  try {
    const extractionPayload: any = {
      file_name: photo.name || `bol-${Date.now()}.${ext}`,
      file_path: fileName,
      document_type: "pending",
      status: "pending" as any,
      uploaded_by: userId || null,
      uploaded_by_email: userEmail || null,
    };
    if (tenantId) extractionPayload.tenant_id = tenantId;

    const { data: extraction, error: extractionErr } = await supabase
      .from("document_extractions")
      .insert(extractionPayload)
      .select("id")
      .single();

    if (extractionErr) {
      console.error("IDP extraction record error:", extractionErr);
      return;
    }

    const { error: fnErr } = await supabase.functions.invoke("process-bol", {
      body: { extraction_id: extraction.id },
    });

    if (fnErr) {
      console.error("IDP process-bol invocation error:", fnErr);
    }
  } catch (err) {
    console.error("IDP pipeline error for yard BOL:", err);
  }
}
